import Skeleton from "react-loading-skeleton";
import Modal from "../../../components/Modal/Modal";
import { useStore } from "../../../store/useStore";
import { Colors } from "../../../utils/constants/style";
import { InfractionConsolidatedTotalsEnum, InfractionTypes } from "../../../store/types/infraction";
import InfractionItem from "../../../components/InfractionItem/InfractionItem";
import InfoFieldBox from "../../../components/InfoFieldBox/InfoFieldBox";
import InfoField from "../../../components/InfoField/InfoField";
import { formatDate, formatSecondsToFullHour } from "../../../utils/functions/formatters";
import { INFRACTION_DATE_TYPES, INFRACTION_TYPES } from "../../../utils/constants/constants";
import Checkbox from "../../../components/FormComponents/Checkbox/Checkbox";
import Tooltip from "../../../components/Tooltip/Tooltip";
import { Calendar2, ClipboardClose, SearchNormal1 } from "iconsax-react";
import InfractionInfo from "../../../components/InfractionInfo/InfractionInfo";
import StackedBarChart from "../../../components/StackedBarChart/StackedBarChart";
import Tippy from "@tippyjs/react";
import FilledButton from "../../../components/FilledButton/FilledButton";

interface InfractionsConsolidatedModalProps {
  onClose: () => void;
}

const InfractionsConsolidatedModal: React.FC<InfractionsConsolidatedModalProps> = ({ onClose }) => {
  const infractionConsolidatedSelected = useStore((state) => state.infraction.infractionConsolidatedSelected);
  const isLoadingInfractionConsolidated = useStore((state) => state.infraction.isLoadingInfractionConsolidated);
  const infractionsTypePerPeriodUserChartData = useStore((state) => state.infraction.infractionsTypePerPeriodUserChartData);
  const isLoadingInfractionsTypePerPeriodUserChartData = useStore((state) => state.infraction.isLoadingInfractionsTypePerPeriodUserChartData);
  const setVisibleInfractionTypes = useStore((state) => state.infraction.setVisibleInfractionTypes);
  const visibleInfractionTypes = useStore((state) => state.infraction.visibleInfractionTypes);
  const selectedDateType = useStore((state) => state.infraction.selectedDateType);
  
  const assembleDaysStructure = (days: any) => {
    if(!Object.keys(days).length) return [];
    const dayKeys = Object.keys(days);
    const parsedDays = {...days};

    if(new Date(dayKeys[0]).getDay() !== 0) {
      let d = new Date(dayKeys[0]);
      let shouldStop = false;
      while(!shouldStop) {
        if(d.getDay() === 0) shouldStop = true;
        parsedDays[d.toISOString()] = [];
        d.setDate(d.getDate() - 1);
      }
    }
    if(new Date(dayKeys[dayKeys.length - 1]).getDay() !== 6) {
      let d = new Date(dayKeys[dayKeys.length - 1]);
      let shouldStop = false;
      while(!shouldStop) {
        if(d.getDay() === 6) shouldStop = true;
        parsedDays[d.toISOString()] = [];
        d.setDate(d.getDate() + 1);
      }
    }

    const parsedDaysArray = [];
    for(const [key, value] of Object.entries(parsedDays)) {
      const [data, total] = groupInfractions(value) as any
      parsedDaysArray.push({
        data,
        total,
        day: new Date(key)
      });
    };

    return parsedDaysArray.sort((a,b) => {
      return a.day.getTime() - b.day.getTime();
    });
  };

  const groupInfractions = (infractions: any) => {
    let extraHourTotal = 0;
    let manualMacrosTotal = 0;
    let alteredMacrosTotal = 0;
    let total = 0;
    const groupedInfractions: any[] = [];

    infractions
    .filter((infraction: any) => visibleInfractionTypes.includes(infraction.type))
    .forEach((infraction: any) => {
      switch(infraction.type) {
        case InfractionTypes.ALTERED_MACRO:
          alteredMacrosTotal ++;
          total++;
          break; 
        case InfractionTypes.MANUAL_MACRO:
          manualMacrosTotal ++;
          total++;
          break; 
        case InfractionTypes.EXTRA_HOUR:
          extraHourTotal += infraction.data.amount;
          total++;
          break;
        case InfractionTypes.INTERJOURNEY_TIME:
        case InfractionTypes.MEAL_TIME:
        case InfractionTypes.LONG_WORKING_TIME:
          groupedInfractions.push({
            type: infraction.type,
            amount: parseInt(infraction.data.duration),
          });
          total++;
          break;
      }
    });

    if(extraHourTotal) {
      groupedInfractions.push({
        type: InfractionTypes.EXTRA_HOUR,
        amount: extraHourTotal,
      })
    }

    if(manualMacrosTotal) {
      groupedInfractions.push({
        type: InfractionTypes.MANUAL_MACRO,
        amount: manualMacrosTotal,
      })
    }

    if(alteredMacrosTotal) {
      groupedInfractions.push({
        type: InfractionTypes.ALTERED_MACRO,
        amount: alteredMacrosTotal,
      })
    }

    return [groupedInfractions, total];
  };

  const handleDayClick = (day: string, shouldRedirectOnClick: boolean) => {
    if(shouldRedirectOnClick) window.open(`/macros?user=${infractionConsolidatedSelected?.user.name}&started_at_begin=${day}&sort_value=asc`, "_blank");
  }

  const handleConsolidatedModalClick = () => {
    window.open("/gerenciador-horas", "_blank");
  }

  const asembleTooltipContent = (type: string, amount: number) => {
    let content = '';
    switch(type) {
      case InfractionTypes.INTERJOURNEY_TIME:
        content = 'Descanso interjornada insuficiente. O recomendado é, ao menos, 11:00:00 e o motorista';
        if(amount === 0) content += ' não descansou'; 
        else content += ` fez apenas ${formatSecondsToFullHour(amount)}`;
        return (
          <div className="flex flex-col p-2">
            <InfractionInfo
              type={InfractionTypes.INTERJOURNEY_TIME}
            />
            {content}
          </div>
        );
      case InfractionTypes.MEAL_TIME:
        content = 'Tempo de refeição insuficiente. O recomendado é, ao menos, 01:00:00 e o motorista';
        if(amount === 0) content += ' não parou para comer'; 
        else content += ` fez apenas ${formatSecondsToFullHour(amount)}`;
        return (
          <div className="flex flex-col p-2">
            <InfractionInfo
              type={InfractionTypes.MEAL_TIME}
            />
            {content}
          </div>
        );
      case InfractionTypes.EXTRA_HOUR:
        content = `Horas extras geradas no dia, somando um total de ${formatSecondsToFullHour(amount)}`;
        return (
          <div className="flex flex-col p-2">
            <InfractionInfo
              type={InfractionTypes.EXTRA_HOUR}
            />
            {content}
          </div>
        );
      case InfractionTypes.LONG_WORKING_TIME:
        content = `Tempo de direção ininterrupta acima do permitido. O recomendado é, no máximo, 05:30:00 e o motorista dirigiu por ${formatSecondsToFullHour(amount)} consecutivas`;
        return (
          <div className="flex flex-col p-2">
            <InfractionInfo
              type={InfractionTypes.LONG_WORKING_TIME}
            />
            {content}
          </div>
        );
      case InfractionTypes.MANUAL_MACRO:
        content = `${amount > 1 ? 'Foram inseridas' : 'Foi inserida'} ${amount} macro${amount > 1 ? 's' : ''} ${amount > 1 ? 'manuais' : 'manual'}`;
        return (
          <div className="flex flex-col p-2">
            <InfractionInfo
              type={InfractionTypes.MANUAL_MACRO}
            />
            {content}
          </div>
        );
      case InfractionTypes.ALTERED_MACRO:
        content = `${amount} macro${amount > 1 ? 's' : ''} ${amount > 1 ? 'sofreram' : 'sofreu'} alterações em relação ao registro original`;
        return (
          <div className="flex flex-col p-2">
            <InfractionInfo
              type={InfractionTypes.ALTERED_MACRO}
            />
            {content}
          </div>
        );
    }
  }

  const daysToRenderList: any[] = assembleDaysStructure(infractionConsolidatedSelected?.days ?? {});
  return (
    <Modal
      title="Calendário de Infrações"
      icon={<Calendar2 width={30} height={30} color={Colors.white} />}
      onClose={onClose}
    >
      { 
        (!isLoadingInfractionConsolidated && !infractionConsolidatedSelected) ? 
        <div className="flex flex-col items-center justify-center gap-5 p-5 mt-5">
          <ClipboardClose color={Colors.gray_3} size={40}/>
          <div className="flex items-center mt-4 justify-center text-center text-lg">
            Para visualizar os detalhes das infrações, vincule o motorista<br></br>a um lote de folha válido dentro do período selecionado
          </div>
          <div className="flex-1">
            <FilledButton className="w-full p-4 mt-4" onClick={handleConsolidatedModalClick}>Gerenciar Lotes de Folha</FilledButton>
          </div>
        </div>
        :
        <div className="flex flex-col gap-5 mt-5 min-w-[1300px]">
          <div className="flex">
            <div className='w-[600px]'>
              <div className="text-dark-blue font-bold mb-5">
                Dados do Registro
              </div>
              <InfoFieldBox>
                <InfoField
                  loading={isLoadingInfractionConsolidated}
                  label="Motorista"
                  content={infractionConsolidatedSelected?.user.name}
                />
                <InfoField
                  loading={isLoadingInfractionConsolidated}
                  label="Competência"
                  content={ formatDate(infractionConsolidatedSelected?.competence_start).substring(3, 10) ?? '-'}
                />
                <InfoField
                  loading={isLoadingInfractionConsolidated}
                  label="Infrações na Competência"
                  content={
                    (infractionConsolidatedSelected?.totals?.altered_macro ?? 0) + 
                    (infractionConsolidatedSelected?.totals?.manual_macro ?? 0) +
                    (infractionConsolidatedSelected?.totals?.interjourney_time ?? 0) +
                    (infractionConsolidatedSelected?.totals?.long_working_time ?? 0) +
                    (infractionConsolidatedSelected?.totals?.meal_time ?? 0)
                  }
                />
              </InfoFieldBox>
              <div className="text-dark-blue font-bold mb-5 mt-7">
                Infrações Visíveis
              </div>
              <div className="flex flex-wrap items-start">
                {
                  INFRACTION_TYPES.map(({label, value, field} : { label: string, value: string, field: unknown }) => {
                    return (
                      <div className="bg-gray_0 p-1 rounded-[7px] mr-2 mb-2">
                        <Checkbox
                          key={value}
                          onChange={() => {
                            let updatedvisibleInfractionTypes = [...visibleInfractionTypes];
                            updatedvisibleInfractionTypes.includes(value) ? updatedvisibleInfractionTypes.splice(updatedvisibleInfractionTypes.indexOf(value), 1) : updatedvisibleInfractionTypes.push(value);
                            if(updatedvisibleInfractionTypes.length) setVisibleInfractionTypes(updatedvisibleInfractionTypes);
                          }}
                          className="w-[17px] h-[17px]"
                          labelClassName="font-light font-base text-lg"
                          checked={visibleInfractionTypes.includes(value)}
                          text={`${label} (${infractionConsolidatedSelected?.totals?.[field as InfractionConsolidatedTotalsEnum]})`}
                          loading={isLoadingInfractionConsolidated}
                        />
                      </div>
                    )
                  })
                }
              </div>
            </div>
            <div className="min-w-[700px]">
              <div className="gap-3 mt-1 w-full">
                  <StackedBarChart
                    title={selectedDateType === INFRACTION_DATE_TYPES[0].value ? "Infrações - Última Semana" : "Infrações - Último Quadrimestre"}
                    data={infractionsTypePerPeriodUserChartData}
                    loading={isLoadingInfractionsTypePerPeriodUserChartData}
                    div='userchartdiv'
                    height={selectedDateType === INFRACTION_DATE_TYPES[0].value ? 450 : 300}
                  />
              </div>
            </div>
          </div>
          <div className="shadow-base py-2 px-5 rounded-[12px] bg-white">
            { daysToRenderList.length ?
              <div className="flex h-full flex-col">
                <div className="flex flex-auto flex-col">
                  <div className="grid grid-cols-7 gap-px border-b border-gray_08 font-semibold text-black">
                    <div className="flex justify-center py-2">
                      <span>D</span>
                      <span className="sr-only sm:not-sr-only">om</span>
                    </div>
                    <div className="flex justify-center py-2">
                      <span>S</span>
                      <span className="sr-only sm:not-sr-only">eg</span>
                    </div>
                    <div className="flex justify-center py-2">
                      <span>T</span>
                      <span className="sr-only sm:not-sr-only">er</span>
                    </div>
                    <div className="flex justify-center py-2">
                      <span>Q</span>
                      <span className="sr-only sm:not-sr-only">ua</span>
                    </div>
                    <div className="flex justify-center py-2">
                      <span>Q</span>
                      <span className="sr-only sm:not-sr-only">ui</span>
                    </div>
                    <div className="flex justify-center py-2">
                      <span>S</span>
                      <span className="sr-only sm:not-sr-only">ex</span>
                    </div>
                    <div className="flex justify-center py-2">
                      <span>S</span>
                      <span className="sr-only sm:not-sr-only">ab</span>
                    </div>
                  </div>
                  <div className="flex text-xs flex-auto">
                    <div className="w-full grid grid-cols-7 grid-rows gap-px">
                      { isLoadingInfractionConsolidated ? 
                        [...Array(30)].map(() => {
                          return (
                            <div className="hover:bg-gray_0 relative px-3 py-2 w-[175px] h-[255px] border-b border-r border-gray_08 cursor-pointer"> 
                              <time><Skeleton height={10} width={30} /></time>
                              <div className="p-1 text-center">
                                <Skeleton height={125} width={125} />
                              </div>
                            </div>
                          );
                        })
                      : (
                        daysToRenderList.map((record) => {
                          const shouldRedirectOnClick = record.data?.length ? true : false;
                          let dayOutOfCompetence = true;
                          if(infractionConsolidatedSelected) {
                            dayOutOfCompetence = record.day < new Date(infractionConsolidatedSelected.competence_start) || record.day > new Date(infractionConsolidatedSelected.competence_end);
                          }
                          return (
                            <div 
                              onClick={() => handleDayClick(record.day.toISOString(), shouldRedirectOnClick)} 
                              className={
                                `rbg-gray_0 relative px-3 py-2 text-gray_3 min-w-[175px] h-[255px] border-b border-r border-gray_08 cursor-pointer
                                ${dayOutOfCompetence ? 'bg-gray_0' : 'hover:bg-gray_0 bg-white'}
                                `
                              }
                            > 
                              <div className="flex justify-between mb-1">
                                  <time dateTime={record.day.toISOString().substring(0, 10)}>{new Date(record.day).getDate()}</time>
                                  { record.total ? <span className="bg-gray_05 w-fit rounded-[8px] text-sm font-bold px-2 py-1">{ record.total } {record.total > 1 ? 'Infrações' : 'Infração'}</span> : null }
                                    <Tippy content={"Clique para ver as macros"} className="px-2 py-1 bg-gray_3 text-sm rounded-md text-white font-medium" duration={1}>
                                      <SearchNormal1 className="text-gray_3 ml-1" size={20} />
                                    </Tippy>
                              </div>
                              { record.data?.length ? 
                                <div>
                                  { record.data.map((infraction: any) => {
                                    return(
                                      <Tooltip
                                        text={asembleTooltipContent(infraction.type, infraction.amount) ?? '-'}
                                        isVisible={true}
                                      >
                                        <div style={{ pointerEvents: "auto" }}>
                                          <InfractionItem 
                                            type={infraction.type}
                                            value={infraction.amount}
                                          />
                                        </div>
                                      </Tooltip>
                                    )
                                  })}
                                </div>
                                : dayOutOfCompetence ? null : <div className="text-center text-gray_1 font-extralight text-sm">Sem infrações</div>
                              }
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              </div>
              : ''
            }
          </div>
        </div>
      }
    </Modal>
  );
};

export default InfractionsConsolidatedModal;
